import { KEYS } from '../../../shared/index.js';
import { isElementFocusable } from '../../../shared/isElementFocusable.js';

import { Modal } from './Modal.js';

export class ModalHelper {
	public static get count() {
		return ModalHelper.modals.length;
	}

	public static get activeModal(): Modal | undefined {
		if (ModalHelper.count > 0) {
			return ModalHelper.modals[ModalHelper.count - 1];
		}
	}

	public static init() {
		if (!ModalHelper.globalEventsBound) {
			ModalHelper.document.addEventListener('keyup', ModalHelper.handleEscape);
			ModalHelper.document.addEventListener('focus', ModalHelper.trapFocus, true);
			ModalHelper.globalEventsBound = true;
		}
	}

	public static addActiveModal(modal: Modal) {
		ModalHelper.modals.push(modal);
		setTimeout(() => {
			if (modal.modalNode) {
				ModalHelper.focusFirstDescendant(modal.modalNode);
			}
		}, 10);
	}

	public static removeActiveModal(modal: Modal) {
		if (ModalHelper.isActiveModal(modal)) {
			ModalHelper.modals.pop();
			if (ModalHelper.count > 0 && ModalHelper.activeModal && ModalHelper.activeModal.modalNode) {
				ModalHelper.focusFirstDescendant(ModalHelper.activeModal.modalNode);
			}
		}
	}

	public static focusFirstDescendant(node: HTMLElement) {
		if (node) {
			for (const child of Array.from(node.childNodes)) {
				if (
					ModalHelper.attemptFocus(child as HTMLElement) ||
					ModalHelper.focusFirstDescendant(child as HTMLElement)
				) {
					return true;
				}
			}
		}
		return false;
	}

	private static globalEventsBound = false;
	private static modals: Modal[] = [];
	private static IgnoreUtilFocusChanges = false;

	private static get document() {
		return document;
	}

	private static isActiveModal(modal: Modal): boolean {
		return modal === ModalHelper.activeModal;
	}

	private static attemptFocus(node: HTMLElement) {
		if (!isElementFocusable(node)) {
			return false;
		}
		ModalHelper.IgnoreUtilFocusChanges = true;
		try {
			node.focus();
		} catch (e) {
			console.error((e as any).message);
		}
		ModalHelper.IgnoreUtilFocusChanges = false;
		return this.document.activeElement === node;
	}

	private static closeActiveModal() {
		if (ModalHelper.activeModal) {
			ModalHelper.activeModal.handleClose();
			return true;
		}
		return false;
	}

	private static focusLastDescendant(node: HTMLElement) {
		if (node) {
			for (const child of Array.from(node.childNodes).reverse()) {
				if (
					ModalHelper.attemptFocus(child as HTMLElement) ||
					ModalHelper.focusLastDescendant(child as HTMLElement)
				) {
					return true;
				}
			}
		}
		return false;
	}

	private static trapFocus = (event: Event) => {
		if (ModalHelper.IgnoreUtilFocusChanges) {
			return;
		}

		const activeModal: Modal | undefined = ModalHelper.activeModal;
		if (activeModal) {
			const eventElement = event.target as Element;
			if (activeModal.modalNode) {
				if (activeModal.modalNode.contains(eventElement)) {
					activeModal.lastFocus = eventElement;
				} else {
					ModalHelper.focusFirstDescendant(activeModal.modalNode);
					if (activeModal.lastFocus === ModalHelper.document.activeElement) {
						ModalHelper.focusLastDescendant(activeModal.modalNode);
					}
					activeModal.lastFocus = ModalHelper.document.activeElement;
				}
			}
		}
	};

	private static handleEscape = (event: KeyboardEvent) => {
		const key = event.key;
		if (key === KEYS.ESCAPE && ModalHelper.closeActiveModal()) {
			event.stopPropagation();
		}
	};
}
